/**
 * The gallery on a product page: thumbnails, swipe, and zoom.
 *
 * Zoom is the part worth explaining. It is a magnifier that follows the pointer rather
 * than a modal lightbox, because the thing people actually zoom for on a pet product is
 * the small print — the ingredients panel, the weight, the batch — and a lightbox makes
 * you lose your place to read it. On touch, where there is no hover, tapping toggles it
 * and dragging moves the magnified point.
 *
 * Order comes from the caller and is not re-sorted here: the platform's curated photos
 * lead and the vendor's own follow, which is the decision that keeps one product looking
 * like one product across every shop selling it.
 */
import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AuthImage } from '@/components/auth-image';

/**
 * Both paths, explicitly.
 *
 * The two sources spell this differently — a curated photo is one path plus a `variant`
 * query, a vendor photo is two separate paths — so the caller resolves it and the gallery
 * assumes neither convention.
 */
export type GalleryImage = {
  full_path: string;
  thumb_path: string;
  /** True for a platform-curated photo. */
  is_curated?: boolean;
};

/** How far the magnifier enlarges. Enough to read a panel, not so much it loses context. */
const ZOOM = 2.5;

export function ProductGallery({ images, alt }: { images: GalleryImage[]; alt: string }) {
  const [index, set_index] = useState(0);
  const [zooming, set_zooming] = useState(false);
  /** Pointer position over the image, 0..1 on each axis. */
  const [origin, set_origin] = useState({ x: 0.5, y: 0.5 });
  const frame = useRef<HTMLDivElement>(null);

  if (images.length === 0) {
    return (
      <div
        className="flex aspect-square w-full items-center justify-center rounded-card bg-secondary/30 text-6xl"
        data-testid="pdp-gallery-empty"
      >
        🐾
      </div>
    );
  }

  const current = images[Math.min(index, images.length - 1)];
  const step = (delta: number) => {
    set_zooming(false);
    set_index((i) => (i + delta + images.length) % images.length);
  };

  /** Accepts either event, since the overlay is clicked and moved over. */
  function track(e: { clientX: number; clientY: number }) {
    const box = frame.current?.getBoundingClientRect();
    if (!box) return;
    set_origin({
      x: Math.min(Math.max((e.clientX - box.left) / box.width, 0), 1),
      y: Math.min(Math.max((e.clientY - box.top) / box.height, 0), 1),
    });
  }

  return (
    <div className="flex flex-col gap-2" data-testid="pdp-gallery">
      <div
        ref={frame}
        className="relative aspect-square w-full overflow-hidden rounded-card border-2 border-brand-outline-variant/15 bg-card"
        onPointerMove={(e) => {
          if (zooming) track(e);
        }}
        onPointerLeave={() => set_zooming(false)}
        data-testid="pdp-gallery-frame"
      >
        {/*
         * The zoomed layer is the SAME image scaled with a transform-origin that follows
         * the pointer, rather than a second larger request: the full variant is already
         * loaded, so magnifying costs nothing and there is no flash of a loading tile
         * under the cursor.
         */}
        {/*
          * The transform belongs on the IMAGE, not on the overlay button.
          *
          * It was on the button first, which is transparent — so the magnification applied
          * to nothing a shopper could see while the hint text claimed it had worked. Worth
          * remembering that a passing assertion on the wrong element proves nothing.
          */}
        <div
          className="h-full w-full transition-transform duration-150"
          style={
            zooming
              ? { transform: `scale(${ZOOM})`, transformOrigin: `${origin.x * 100}% ${origin.y * 100}%` }
              : undefined
          }
          data-testid="pdp-zoom-layer"
        >
          <AuthImage
            key={current.full_path}
            path={current.full_path}
            alt={alt}
            className="h-full w-full object-contain"
            data-testid="pdp-gallery-image"
          />
        </div>
        <button
          type="button"
          aria-label={zooming ? 'Zoom out' : 'Zoom in'}
          onClick={(e) => {
            track(e);
            set_zooming((z) => !z);
          }}
          className={cn('absolute inset-0', zooming ? 'cursor-zoom-out' : 'cursor-zoom-in')}
          data-testid="pdp-zoom-toggle"
        />
        {zooming && (
          <span
            className="pointer-events-none absolute bottom-2 left-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white"
            data-testid="pdp-zoom-hint"
          >
            Move to look around · tap to exit
          </span>
        )}
        {!zooming && (
          <span className="pointer-events-none absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-black/45 px-2 py-0.5 text-[11px] font-medium text-white">
            <ZoomIn className="h-3 w-3" /> Tap to zoom
          </span>
        )}

        {images.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous photo"
              onClick={() => step(-1)}
              className="absolute left-1.5 top-1/2 -translate-y-1/2 rounded-full bg-card/90 p-1.5 shadow-clay-sm active:scale-90"
              data-testid="pdp-prev"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Next photo"
              onClick={() => step(1)}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-card/90 p-1.5 shadow-clay-sm active:scale-90"
              data-testid="pdp-next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <span
              className="pointer-events-none absolute bottom-2 right-2 rounded-full bg-black/45 px-2 py-0.5 text-[11px] font-medium text-white"
              data-testid="pdp-counter"
            >
              {index + 1} / {images.length}
            </span>
          </>
        )}
      </div>

      {images.length > 1 && (
        <ul className="flex gap-2 overflow-x-auto pb-1" data-testid="pdp-thumbs">
          {images.map((img, i) => (
            <li key={img.thumb_path}>
              <button
                type="button"
                onClick={() => {
                  set_zooming(false);
                  set_index(i);
                }}
                aria-label={`Photo ${i + 1}`}
                aria-current={i === index}
                className={cn(
                  'h-16 w-16 shrink-0 overflow-hidden rounded-input border-2 transition-colors',
                  i === index ? 'border-primary' : 'border-brand-outline-variant/20 hover:border-primary/40',
                )}
                data-testid={`pdp-thumb-${i}`}
              >
                <AuthImage path={img.thumb_path} alt="" className="h-full w-full object-cover" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
